import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useAlert } from '../context/AlertContext';
import { customFetcher } from '../hooks/useFetch';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { NotificationPreferences } from '../Admin/UserSetting/User/Childs/NotificationPreferences';
import { ITopic } from '../Interfaces/ITopic';
import {
  optionsToPreferences,
  preferencesToOptions,
} from '../services/notificationTopics.service';
import { USER_TOPICS_CHANGED_EVENT } from '../services/notifications.service';

interface IAccountNotificationSettings {
  Options: number[];
  ForcedByGroup: number[];
  ShowNotificationsAtLogin: boolean;
}

export const AccountNotifications: React.FC = () => {
  const { showAlert } = useAlert();

  const [topics, setTopics] = useState<ITopic[] | undefined>(undefined);
  const [settings, setSettings] = useState<IAccountNotificationSettings | null>(
    null
  );
  const [preferences, setPreferences] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [showAtLogin, setShowAtLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Themenliste und eigene Einstellungen zusammen laden — ohne die Themen
  // laesst sich die Bit-Auswahl nicht in Schalter uebersetzen.
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [topicResponse, settingsResponse] = await Promise.all([
        customFetcher({ endpoint: '/topic' }) as Promise<ITopic[]>,
        customFetcher({
          endpoint: '/account/notifications',
        }) as Promise<IAccountNotificationSettings>,
      ]);
      const topicArray = Array.isArray(topicResponse) ? topicResponse : [];
      setTopics(topicArray);
      setSettings(settingsResponse);
      setPreferences(
        optionsToPreferences(topicArray, settingsResponse.Options || [])
      );
      setShowAtLogin(Boolean(settingsResponse.ShowNotificationsAtLogin));
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Benachrichtigungseinstellungen konnten nicht geladen werden.',
      });
      setTopics(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    void load();
  }, [load]);

  // Weicht die Maske vom letzten gespeicherten Stand ab, hat der Benutzer
  // ungespeicherte Schalter offen.
  const hasUnsavedChanges = useCallback(() => {
    if (!settings || !topics) return false;
    if (Boolean(settings.ShowNotificationsAtLogin) !== showAtLogin) return true;
    const saved = optionsToPreferences(topics, settings.Options || []);
    return topics.some(
      topic =>
        Boolean(saved[topic.idTopic]) !== Boolean(preferences[topic.idTopic])
    );
  }, [settings, topics, preferences, showAtLogin]);

  // Meldet der Server geaenderte Themen-Abos — weil der Administrator sie in
  // der Benutzerverwaltung umgestellt hat oder weil in einem anderen Tab
  // gespeichert wurde —, liest die Maske neu. Sonst zeigt sie weiter den Stand
  // von ihrem Aufbau, und der naechste Klick auf Speichern schreibt ihn zurueck
  // (QS #18, Testfall 3). Wer gerade selbst Schalter umgestellt hat, behaelt
  // sie: eine halbfertige Eingabe unter den Haenden wegzuziehen waere
  // schlimmer als ein kurz veralteter Stand.
  useEffect(() => {
    const reload = () => {
      if (hasUnsavedChanges()) return;
      void load();
    };
    window.addEventListener(USER_TOPICS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(USER_TOPICS_CHANGED_EVENT, reload);
  }, [load, hasUnsavedChanges]);

  const reset = useCallback(() => {
    if (!settings || !topics) return;
    setPreferences(optionsToPreferences(topics, settings.Options || []));
    setShowAtLogin(Boolean(settings.ShowNotificationsAtLogin));
  }, [settings, topics]);

  const save = useCallback(async () => {
    if (!topics) return;
    setIsSaving(true);
    try {
      const updated = (await customFetcher({
        endpoint: '/account/notifications',
        method: 'PUT',
        body: {
          Options: preferencesToOptions(preferences),
          ShowNotificationsAtLogin: showAtLogin,
        },
      })) as IAccountNotificationSettings;
      setSettings(updated);
      setPreferences(optionsToPreferences(topics, updated.Options || []));
      setShowAtLogin(Boolean(updated.ShowNotificationsAtLogin));
      showAlert({ text: 'Benachrichtigungseinstellungen gespeichert.' });
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? e.message
            : 'Benachrichtigungseinstellungen konnten nicht gespeichert werden.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [preferences, showAtLogin, showAlert, topics]);

  if (isLoading) return <LoadingSpinner height='40vh' />;

  return (
    <Card variant='outlined'>
      <CardContent>
        <NotificationPreferences
          topics={topics}
          preferences={preferences}
          setPreferences={setPreferences}
          showAtLogin={showAtLogin}
          setShowAtLogin={setShowAtLogin}
          forcedByGroup={settings?.ForcedByGroup || []}
          groupByReferenceType
        />
        {topics !== undefined && (
          <Box
            sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          >
            <Button variant='outlined' onClick={reset} disabled={isSaving}>
              Abbruch
            </Button>
            <Button variant='contained' onClick={save} disabled={isSaving}>
              {isSaving ? <CircularProgress size={18} /> : 'Speichern'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
